import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import { z } from "zod";

const SAS_TOKEN_LEGACY = "SAS_TOKEN";
const SAS_TOKEN_EXPIRES_ON_LEGACY = "SAS_TOKEN_EXPIRES_ON";

const sasTokenKey = (containerName) => `SAS_TOKEN_${containerName}`;
const sasTokenExpiresOnKey = (containerName) =>
  `SAS_TOKEN_EXPIRES_ON_${containerName}`;

const containerName = "documents";

export const getFileIcon = (file) => {
  if (file?.type?.includes?.("pdf")) return "pi pi-file-pdf";
  else if (file?.type?.includes?.("msword") || file?.type?.includes?.("word"))
    return "pi pi-file-word";
  else if (
    file?.type?.includes?.("png") ||
    file?.type?.includes?.("jpg") ||
    file?.type?.includes?.("jpeg")
  )
    return "pi pi-image";
  else if (
    file?.type?.includes?.("csv") ||
    file?.type?.includes?.("excel") ||
    file?.type?.includes?.("spreadsheet")
  )
    return "pi pi-file-excel";
  else return "pi pi-file";
};

export const downloadFileHandler = async (
  file,
  containerName = "documents",
) => {
  try {
    const blobUrl = file.blob_url.split("?")[0];
    const downloadUrl = await fetchBlobUrl(blobUrl, containerName);
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = file.metadata?.original_file_name
      ? decodeURIComponent(file.metadata.original_file_name)
      : file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading the file:", error);
  }
};
export const getDocumentLabel = (filename) => {
  const rawExt = filename.split(".").pop() || "";
  const extension = rawExt
    .replace(/\s*\([^)]*\)$/, "")
    .trim()
    .toLowerCase();
  const extensionMap = {
    jpg: "Image Document",
    jpeg: "Image Document",
    png: "Image Document",
    pdf: "PDF Document",
    doc: "Word Document",
    docx: "Word Document",
    xls: "Excel Document",
    xlsx: "Excel Document",
    ppt: "PPT Document",
    pptx: "PPT Document",
    txt: "Text Document",
    csv: "CSV Document",
  };
  return extensionMap[extension] || "Other Document";
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "kb", "mb", "gb", "tb"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * @param {string} containerName
 * @param {{ forceRefresh?: boolean, entityId?: string, serviceLineCode?: string, fileName?: string }} [options]
 * @returns {Promise<string>}
 */
export const getSasToken = async (containerName, options = {}) => {
  const { entityId, serviceLineCode, fileName, ...restOptions } = options;
  const hasScopeParams = entityId && serviceLineCode && fileName;

  const key = sasTokenKey(containerName);
  const expiresKey = sasTokenExpiresOnKey(containerName);

  const isTokenValid = (token, expiresOn) => {
    if (!token || !expiresOn) return false;
    const now = new Date();
    const expiry = new Date(expiresOn);
    const bufferMs = 60 * 1000;
    return now.getTime() + bufferMs < expiry.getTime();
  };

  const saveToken = (token, expiresOn) => {
    sessionStorage.setItem(key, token);
    sessionStorage.setItem(expiresKey, expiresOn);
  };

  // Skip cache for scoped tokens (they are file-specific)
  if (!restOptions.forceRefresh && !hasScopeParams) {
    let sasToken = sessionStorage.getItem(key);
    let expiresOn = sessionStorage.getItem(expiresKey);
    if (!sasToken || !expiresOn) {
      sasToken = sessionStorage.getItem(SAS_TOKEN_LEGACY);
      expiresOn = sessionStorage.getItem(SAS_TOKEN_EXPIRES_ON_LEGACY);
      if (sasToken && expiresOn) saveToken(sasToken, expiresOn);
    }
    if (isTokenValid(sasToken, expiresOn)) {
      return sasToken;
    }
  }

  const scopeParams = hasScopeParams
    ? { entityId, serviceLineCode, fileName }
    : {};
  const newTemporaryToken = await fetchTemporarySasToken(
    containerName,
    scopeParams,
  );

  // Only cache non-scoped tokens
  if (!hasScopeParams) {
    saveToken(newTemporaryToken.sasToken, newTemporaryToken.expiresOn);
  }
  return newTemporaryToken.sasToken;
};

/**
 * Get a scoped SAS token for document uploads.
 * Returns the full server response including server-generated blobName and blobUrl.
 * @param {string} containerName
 * @param {{ entityId: string, serviceLineCode: string, fileName: string }} scopeParams
 * @returns {Promise<{ containerName: string, blobName: string, documentUuid: string, sasToken: string, expiresOn: string, blobUrl: string }>}
 */
export const getScopedUploadToken = async (containerName, scopeParams) => {
  const tokenData = await fetchTemporarySasToken(containerName, scopeParams);
  return tokenData;
};

const fetchTemporarySasToken = async (containerName, scopeParams = {}) => {
  try {
    let url = API_ROUTES.FETCH_TEMPORARY_TOKEN;

    // Append scoped query params for containers that require them (e.g. 'documents')
    const queryParts = [];
    if (scopeParams.entityId)
      queryParts.push(`entityId=${encodeURIComponent(scopeParams.entityId)}`);
    if (scopeParams.serviceLineCode)
      queryParts.push(
        `serviceLineCode=${encodeURIComponent(scopeParams.serviceLineCode)}`,
      );
    if (scopeParams.fileName)
      queryParts.push(`fileName=${encodeURIComponent(scopeParams.fileName)}`);
    if (queryParts.length > 0) {
      url = `${url}?${queryParts.join("&")}`;
    }

    const data = await apiService(url, "GET", {
      replace: { container: containerName },
    });
    return data?.data;
  } catch (err) {
    console.log(`An unexpected error occured while fetching the token: ${err}`);
  }
};

export const openFileInNewTab = async ({ objectURL }) => {
  try {
    const response = await fetch(objectURL);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (err) {
    console.error("Error opening preview:", err);
  }
};

export const openPreviewInNewTab = async (
  file,
  containerName = "documents",
) => {
  try {
    const blobUrl = file.objectURL.split("?")[0];
    const downloadUrl = await fetchBlobUrl(blobUrl, containerName);
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (error) {
    console.error("Error opening preview:", error);
  }
};

export const removeToken = () => {
  sessionStorage.removeItem(SAS_TOKEN_LEGACY);
  sessionStorage.removeItem(SAS_TOKEN_EXPIRES_ON_LEGACY);
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith("SAS_TOKEN_") || k?.startsWith("SAS_TOKEN_EXPIRES_ON_")) {
      keysToRemove.push(k);
    }
  }
  keysToRemove.forEach((k) => sessionStorage.removeItem(k));
};

const fetchBlobUrl = async (blobUrl, containerName) => {
  try {
    const data = await apiService(API_ROUTES.FETCH_BLOB_URL, "GET", {
      replace: { blobUrl, containerName },
      schema: z.object({
        blobUrl: z.string(),
        containerName: z.string(),
      }),
    });
    return data?.data?.baseUrl;
  } catch (err) {
    console.log(
      "An unexpected error occurred whiled fetching blob url ",
      err.message,
    );
    throw err;
  }
};

export const getFilenameFromReference = (fileReference) => {
  if (!fileReference) return "";
  try {
    const url = new URL(fileReference);
    const pathname = url.pathname;
    const filename = pathname.substring(pathname.lastIndexOf("/") + 1);
    return decodeURIComponent(filename);
  } catch (error) {
    console.error("Error extracting filename from reference:", error);
    return "";
  }
};
export const PayslipStatusMap = {
  pending: "Processing",
  rejected: "Rejected",
  approved: "Approved",
  released: "Released",
  matched: "Awaiting Review",
  unmatched: "Awaiting Review",
  no_match: "Awaiting Review",
  extraction_failed: "Awaiting Review",
};

export const getPayslipBlobName = (fileName) => {
  return fileName.split("/").pop();
};

export const getAdHocEscalationDisplayName = (
  subCategoryName,
  date,
  fileName,
) => {
  try {
    if (!subCategoryName || !date || !fileName) return fileName;
    const extension = fileName.split(".").pop().toLowerCase();
    const month = new Date(date).toLocaleString("default", { month: "long" });
    const year = new Date(date).getFullYear();
    const extUpper = extension ? extension.toUpperCase() : "";
    return `${subCategoryName} - ${month} ${year}.${extension}${extUpper ? ` (${extUpper})` : ""}`;
  } catch (error) {
    console.error("Error generating display name:", error);
    return fileName;
  }
};
